import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getInvoices } from '@/modules/Invoice/services'
import { INVOICE_STATUS_OPTIONS } from '@/modules/Invoice/constants'
import { GetInvoicesRow } from '@/modules/Invoice/types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { generatePathDatacentral, paths } from '@/utils/path'
import { Typogaphy } from 'vnetwork-ui'
import { CheckCircle, ArrowRight, Banknote } from 'lucide-react'
import { formatCurrency, formatFromUTC } from '@/utils/function'

const RecentlyPaidInvoicesWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    // Fetch recently paid invoices - sorted by payment date desc
    const { data: paidData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, 'recently-paid-dashboard'],
        queryFn: () =>
            getInvoices({
                page: 1,
                page_size: 10,
                q: '',
                filters: btoa(
                    JSON.stringify({
                        invoice_status: [INVOICE_STATUS_OPTIONS.Paid],
                    })
                ),
            }),
    })

    const paidInvoices = paidData?.results || []
    const totalPaid = paidData?.pagination?.total || 0

    // Calculate total amount from visible invoices
    const totalAmount = paidInvoices
        .slice(0, 5)
        .reduce((sum, inv) => sum + (inv.invoice_total || 0), 0)

    const handleInvoiceClick = (invoiceId: number) => {
        navigate(
            generatePathDatacentral(paths.invoiceDetail, { id: invoiceId })
        )
    }

    const handleViewAll = () => {
        navigate(
            generatePathDatacentral(
                paths.invoices,
                {},
                {
                    filters: btoa(
                        JSON.stringify({
                            invoice_status: [INVOICE_STATUS_OPTIONS.Paid],
                        })
                    ),
                }
            )
        )
    }

    const renderInvoiceItem = (invoice: GetInvoicesRow) => {
        return (
            <div
                key={invoice.invoice_id}
                onClick={() => handleInvoiceClick(invoice.invoice_id)}
                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
            >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="p-1.5 rounded-full flex-shrink-0 bg-green-100">
                        <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs text-gray-900 truncate">
                            {invoice.customer?.customer_name ||
                                invoice.customer?.company_name ||
                                `#${invoice.invoice_id}`}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate">
                            {formatCurrency(invoice.invoice_total)}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-[10px] text-gray-500">
                        {invoice.invoice_payment_at
                            ? formatFromUTC(invoice.invoice_payment_at, 'DD/MM')
                            : '-'}
                    </span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                </div>
            </div>
        )
    }

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Title */}
                <div className="flex items-center gap-2 mb-3 flex-shrink-0">
                    <Banknote className="w-5 h-5 text-green-500" />
                    <Typogaphy className="font-semibold text-primary">
                        {t('widgets.invoices.recently_paid', 'Mới thanh toán')}
                    </Typogaphy>
                </div>

                {isLoading ? (
                    <div className="space-y-2 flex-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-12 rounded-lg" />
                        ))}
                    </div>
                ) : (
                    <>
                        {/* Summary */}
                        <div
                            className="flex items-center justify-between gap-2 p-2.5 rounded-lg mb-3 flex-shrink-0 bg-green-50"
                            data-tour="recently-paid-summary"
                        >
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <div>
                                    <div className="text-lg font-bold text-green-600">
                                        {totalPaid}
                                    </div>
                                    <div className="text-[10px] text-green-600">
                                        {t(
                                            'widgets.invoices.total_paid',
                                            'Hoá đơn đã thanh toán'
                                        )}
                                    </div>
                                </div>
                            </div>
                            {totalAmount > 0 && (
                                <div className="text-right">
                                    <div className="text-sm font-semibold text-green-700">
                                        {formatCurrency(totalAmount)}
                                    </div>
                                    <div className="text-[10px] text-green-600">
                                        {t(
                                            'widgets.invoices.top_5_total',
                                            'Top 5'
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* List */}
                        <div className="space-y-1.5 flex-1 overflow-auto">
                            {paidInvoices.length === 0 ? (
                                <div className="text-center py-4 text-gray-500 text-sm">
                                    {t(
                                        'widgets.invoices.no_paid_invoices',
                                        'Chưa có hoá đơn thanh toán'
                                    )}
                                </div>
                            ) : (
                                paidInvoices
                                    .slice(0, 5)
                                    .map((invoice) =>
                                        renderInvoiceItem(invoice)
                                    )
                            )}
                        </div>

                        {/* View All */}
                        {totalPaid > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={handleViewAll}
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} ({totalPaid})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default RecentlyPaidInvoicesWidget
